import ArrowUploadSetup from '@/components/icons/ArrowUploadSetup'
import useIsMountedRef from '@/hooks/useIsMountedRef'
import { Box, BoxProps, Stack, Typography } from '@mui/material'
import clsx from 'clsx'
import { memo, useCallback } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'

// 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
// 'application/vnd.ms-excel',
// 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
// 'application/msword',
// 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
// 'application/vnd.ms-powerpoint',
// 'application/pdf'

interface Props {
  multiple?: boolean
  disabled?: boolean
  className?: string
  title?: string
  formats?: string[]
  accept?: Accept
  onChange: (file: File[]) => void
  BoxProps?: BoxProps
  filePath?: any
  /**
   * Draws the control as a drop panel — a dashed area with the file type and
   * the drag affordance stated — rather than as a single line of text. The
   * line is what the older Mk2 modal uses; the panel is what a file chooser
   * looks like everywhere else.
   */
  panel?: boolean
  /** What the panel says under its label, e.g. the accepted extension. */
  hint?: string
}

const SelectExcelFile = memo(
  ({
    title,
    multiple,
    disabled,
    className,
    onChange,
    BoxProps = {},
    accept,
    formats,
    filePath,
    panel = false,
    hint,
  }: Props) => {
    const { t } = useTranslation()
    const isMountedRef = useIsMountedRef()

    const handleDrop = useCallback(
      async (acceptedFiles: File[]) => {
        let hasError = false
        for (const file of acceptedFiles) {
          if (formats?.length && !formats.includes(file.type)) {
            hasError = true
            break
          }
        }
        if (hasError || !acceptedFiles?.length) return
        onChange(acceptedFiles)
      },
      // eslint-disable-next-line
      [isMountedRef, onChange],
    )

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept,
      onDrop: handleDrop,
      multiple: multiple,
      disabled: disabled,
    })

    if (panel) {
      return (
        <Box
          className={clsx(classes.panel, className, {
            [classes.panelActive]: isDragActive,
            [classes.panelFilled]: Boolean(filePath),
            [classes.disabled]: disabled,
          })}
          {...BoxProps}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Stack alignItems="center" spacing={0.5}>
            <ArrowUploadSetup className={classes.panelIcon} />
            <Typography className={classes.panelTitle}>
              {filePath ||
                (isDragActive
                  ? t('decoderModalSelectExcelDropHere')
                  : t('decoderModalSelectExcelChooseFile'))}
            </Typography>
            <Typography className={classes.panelHint}>
              {filePath ? t('decoderModalSelectExcelChangeFile') : hint}
            </Typography>
          </Stack>
        </Box>
      )
    }

    return (
      <Box
        className={clsx(classes.root, className, {
          [classes.disabled]: disabled,
        })}
        {...BoxProps}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          height="44px"
        >
          <Typography className={classes.title}>
            {filePath || t('decoderModalSelectExcelSelectFile')}
          </Typography>
          {!filePath && <ArrowUploadSetup sx={{ mt: '-5px' }} />}
        </Stack>
      </Box>
    )
  },
)

export default SelectExcelFile
